import { observer } from 'mobx-react'
import React from 'react'
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom'

import { useGetPipelineCallsDetails } from '@flowwai-frontend/bps-officer-web/calls'
import {
   ADVANCED_ANALYTICS_DASHBOARDS_ROUTE_PATH,
   ANALYTICS_COPILOT_PATH,
   APPLICATIONS_ASSIGNED_BY_FRIEND_ROUTE_PATH,
   CALLS_ROUTE_PATH,
   CONTACT_BOARD_PATH,
   DASHBOARD_DETAILED_VIEW_ROUTE_PATH,
   DASHBOARD_GRAPH_DETAILED_VIEW_ROUTE_PATH,
   HOME_BOARD_ROUTE_PATH,
   LIST_ROUTE_PATH,
   NOTIFICATIONS_ROUTE_PATH,
   PINNED_PIPELINE_ITEMS_ROUTE_PATH,
   REMINDERS_PATH,
   GIS_ROUTE_PATH,
   PMU_USERS_LOGS_ROUTE_PATH,
   PMU_USERS_ROUTE_PATH,
   ON_HOLD_TDR_CERTIFICATE_ROUTE_PATH,
   CERTIFICATES_LOGS_ROUTE_PATH,
   RELEASE_CERTIFICATE_ROUTE_PATH,
   TEMPLATES_BOARDS_ROUTE_PATH,
   PMU_APPLICATION_MANAGEMENT_ROUTE_PATH,
   TEMPLATES_SUFFIX,
   LEGACY_APPLICATIONS_ROUTE_PATH,
   TDR_LEGACY_APPLICATIONS_ROUTE_PATH,
   SEARCH_AND_MONITORING_ROUTE_PATH,
   GENERATE_SCRUTINY_KEY_ROUTE_PATH,
   TEMPLATE_TDR_DIGITALIZATION_ROUTE_PATH
} from '@flowwai-frontend/bps-officer-web/common/data-access'
import {
   isAISupportWebEnabled,
   isAnalyticsCoPilotWebEnabled,
   enableLegacyApplications,
   isSearchAndMonitoringEnabled,
   isGISTabEnabled,
   isNotificationsWebEnabled,
   isAssignFriendEnabled,
   DashboardMaintenance,
   enableTDRLegacyApplications
} from '@flowwai-frontend/bps-officer-web/common/ui'
import { useGetFeatureGates } from '@flowwai-frontend/shared-web/feature-toggles'
import { Config } from '@flowwai-frontend/bps-officer-web/env-config'
import {
   useUserAppConfigStore,
   useIsDashboardAdmin
} from '@flowwai-frontend/bps-officer-web/iam'
import { AIActionCenter } from '@flowwai-frontend/bps-officer-web/ai-chat-support/ui'
import { SuspenseWithLoader } from '@flowwai-frontend/shared/common-components'
import { usePipelineStore } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import { isPinnedBoardsEnabled } from '@flowwai-frontend/bps-officer-web/navbar'
import { useGetSearchAndMonitoringConfigCache } from '@flowwai-frontend/bps-officer-web/search-and-monitoring/data-access'

// Lazy loading named exports from '@flowwai-frontend/bps-officer-web/analytics'
const AdvancedAnalyticsDashboardsRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/analytics').then(module => ({
      default: module.AdvancedAnalyticsDashboardsRoute
   }))
)
const DashboardDetailedViewRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/analytics').then(module => ({
      default: module.DashboardDetailedViewRoute
   }))
)
const DashboardGraphDetailedViewRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/analytics').then(module => ({
      default: module.DashboardGraphDetailedViewRoute
   }))
)
const DashboardMaintenanceGuard = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/analytics').then(module => ({
      default: module.DashboardMaintenanceGuard
   }))
)

// Lazy loading named exports from '@flowwai-frontend/bps-officer-web/boards'
const AllRemindersRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.AllRemindersRoute
   }))
)
const BoardListRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.BoardListRoute
   }))
)
const BoardRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.BoardRoute
   }))
)
const PinnedBoardRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/boards').then(module => ({
      default: module.PinnedBoardRoute
   }))
)

// Lazy loading named exports from '@flowwai-frontend/bps-officer-web/calls'
const CallLogsBoardRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/calls').then(module => ({
      default: module.CallLogsBoardRoute
   }))
)

// Lazy loading named exports from '@flowwai-frontend/bps-officer-web/data-co-pilot/ui'
const DataCoPilotRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/data-co-pilot/ui').then(
      module => ({
         default: module.DataCoPilotRoute
      })
   )
)

// Lazy loading named exports from '@flowwai-frontend/bps-officer-web/manager-app'
const PageNotFound = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/manager-app').then(module => ({
      default: module.PageNotFound
   }))
)

// Lazy loading named exports from '@flowwai-frontend/bps-officer-web/scheduling'
const HomeBoardRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/scheduling').then(module => ({
      default: module.HomeBoardRoute
   }))
)

// Lazy loading named exports from '@flowwai-frontend/bps-officer-web/notifications'
const NotificationsRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/notifications').then(module => ({
      default: module.NotificationsRoute
   }))
)

// Lazy loading default exports from '../GISRoute'
const GISRoute = React.lazy(() =>
   import('../GISRoute/GISRoute').then(module => ({ default: module.default }))
)

// Lazy loading named exports from '@flowwai-frontend/bps-officer-web/assign-friend/ui'
const ApplicationsAssignedByFriendRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/assign-friend/ui').then(
      module => ({
         default: module.ApplicationsAssignedByFriendRoute
      })
   )
)

const CRMItemSidePeekController = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/application-detailed-view').then(
      module => ({
         default: module.CRMItemSidePeekController
      })
   )
)

// Lazy loading Default exports from '../TemplatesNestedRoute'
const TemplatesNestedRoute = React.lazy(() =>
   import('../TemplatesNestedRoute/TemplatesNestedRoute').then(module => ({
      default: module.default
   }))
)

const OfficersRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/pmu-portal/ui').then(module => ({
      default: module.PMUOfficersRoute
   }))
)

const TDRDigitalizationBoardRoute = React.lazy(() =>
   import('../TDRDigitalizationBoardRoute/TDRDigitalizationBoardRoute').then(
      module => ({
         default: module.default
      })
   )
)

const OfficerLogsRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/pmu-portal/ui').then(module => ({
      default: module.PMUOfficerLogRoute
   }))
)

const ApplicationManagementRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/pmu-portal/ui').then(module => ({
      default: module.ApplicationManagementRoute
   }))
)

const OnHoldTDRCertificateRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/pmu-portal/ui').then(module => ({
      default: module.OnHoldTDRCertificateRoute
   }))
)

const CertificatesLogsRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/pmu-portal/ui').then(module => ({
      default: module.CertificatesLogsRoute
   }))
)

const ReleaseTDRCertificateRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/pmu-portal/ui').then(module => ({
      default: module.ReleaseTDRCertificateRoute
   }))
)

const LegacyApplicationsRoute = React.lazy(() =>
   import(
      '@flowwai-frontend/bps-officer-web/legacy-application/ui'
   ).then(module => ({ default: module.LegacyApplicationsRoute }))
)

const TDRLegacyApplicationsRoute = React.lazy(() =>
   import(
      '@flowwai-frontend/bps-officer-web/legacy-application/ui'
   ).then(module => ({ default: module.TDRLegacyApplicationsRoute }))
)

//region: search and monitoring
const SearchAndMonitoringRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/search-and-monitoring/ui').then(
      module => ({
         default: module.SearchAndMonitoringRoute
      })
   )
)
//endregion

const GenerateScrutinyKeyRoute = React.lazy(() =>
   import('@flowwai-frontend/bps-officer-web/application-detailed-view').then(
      module => ({
         default: module.GenerateScrutinyKeyRoute
      })
   )
)

const HomeNestedRoutes = () => {
   const { path } = useRouteMatch()

   const userAppConfigStore = useUserAppConfigStore()
   const isDashboardAdmin = useIsDashboardAdmin()
   const { isCallsEnabled } = useGetPipelineCallsDetails()

   const { filterFields } = useGetSearchAndMonitoringConfigCache()

   const pipelineStore = usePipelineStore()

   const {
      isApplicationManagementGateEnabled,
      isPMUPortalGateEnabled
   } = useGetFeatureGates()

   const isContactsEnabled =
      userAppConfigStore.permissions?.isContactsEnabled ?? true

   const isSchedulesEnabled =
      userAppConfigStore.permissions?.isSchedulingEnabled &&
      Config.ENABLE_HOME_BOARD_ROUTE === 'true'

   const isDataCoPilotEnabled =
      !!userAppConfigStore.permissions?.isDataCoPilotEnabled &&
      isAnalyticsCoPilotWebEnabled

   const isAIChatSupportEnabled =
      !!userAppConfigStore.permissions?.isAIChatSupportEnabled &&
      isAISupportWebEnabled

   const isPMUOfficer =
      userAppConfigStore.permissions?.userRole === 'PMU_OFFICER' &&
      isPMUPortalGateEnabled

   const isApplicationManagementEnabled =
      userAppConfigStore.permissions?.isApplicationManagementEnabled &&
      isApplicationManagementGateEnabled

   const isTdrManagementEnabled =
      userAppConfigStore.permissions?.isTdrManagementEnabled

   const isSearchAndMonitoringRouteEnabled =
      isSearchAndMonitoringEnabled && filterFields.length > 0

   const isScrutinyKeyGenerationEnabled = !!userAppConfigStore.permissions
      ?.isScrutinyKeyGenerationEnabled

   const getDefaultPath = (): string => {
      if (isSchedulesEnabled) return HOME_BOARD_ROUTE_PATH

      if (userAppConfigStore.salesPipelinesIds?.length) {
         const pipelineId = userAppConfigStore.salesPipelinesIds[0]

         const templateId = pipelineStore.getPipeline(pipelineId)
            .pipelineItemTemplateId

         return `/${TEMPLATES_SUFFIX}/${templateId}`
      }

      // TODO: need to properly handle the default case as well
      return ''
   }

   // Dashboard routes - accessible to dashboard admins
   const dashboardRoutes = [
      <Route
         exact
         key={ADVANCED_ANALYTICS_DASHBOARDS_ROUTE_PATH}
         path={ADVANCED_ANALYTICS_DASHBOARDS_ROUTE_PATH}
      >
         <DashboardMaintenanceGuard>
            <AdvancedAnalyticsDashboardsRoute />
         </DashboardMaintenanceGuard>
      </Route>,
      <Route
         exact
         key={DASHBOARD_DETAILED_VIEW_ROUTE_PATH}
         path={DASHBOARD_DETAILED_VIEW_ROUTE_PATH}
      >
         <DashboardMaintenanceGuard>
            <DashboardDetailedViewRoute />
         </DashboardMaintenanceGuard>
      </Route>,
      <Route
         exact
         key={DASHBOARD_GRAPH_DETAILED_VIEW_ROUTE_PATH}
         path={DASHBOARD_GRAPH_DETAILED_VIEW_ROUTE_PATH}
      >
         <DashboardMaintenanceGuard>
            <DashboardGraphDetailedViewRoute />
         </DashboardMaintenanceGuard>
      </Route>
   ]

   // Non-dashboard routes - hidden from dashboard admins
   const regularRoutes = [
      // Analytics Co-pilot (first in original order)
      ...(isDataCoPilotEnabled
         ? [
              <Route
                 exact
                 key={ANALYTICS_COPILOT_PATH}
                 path={ANALYTICS_COPILOT_PATH}
              >
                 <DataCoPilotRoute />
              </Route>
           ]
         : []),

      // Generate Scrutiny Key route (conditional based on feature enabled)
      ...(isScrutinyKeyGenerationEnabled
         ? [
              <Route
                 exact
                 key={GENERATE_SCRUTINY_KEY_ROUTE_PATH}
                 path={GENERATE_SCRUTINY_KEY_ROUTE_PATH}
              >
                 <GenerateScrutinyKeyRoute />
              </Route>
           ]
         : []),

      // Contacts route (conditional based on permissions)
      ...(isContactsEnabled
         ? [
              <Route exact key={CONTACT_BOARD_PATH} path={CONTACT_BOARD_PATH}>
                 <BoardRoute />
              </Route>
           ]
         : []),

      // Calls route (conditional based on feature and permissions)
      ...(Config.ENABLE_CALL_LOGS_FEATURE === 'true' && isCallsEnabled
         ? [
              <Route key={CALLS_ROUTE_PATH} path={CALLS_ROUTE_PATH}>
                 <CallLogsBoardRoute />
              </Route>
           ]
         : []),

      // Home board route (conditional based on scheduling enabled)
      <Route exact key={HOME_BOARD_ROUTE_PATH} path={HOME_BOARD_ROUTE_PATH}>
         {isSchedulesEnabled ? (
            <HomeBoardRoute />
         ) : (
            <Redirect to={getDefaultPath()} />
         )}
      </Route>,

      // Pinned boards (conditional based on feature toggle)
      ...(isPinnedBoardsEnabled
         ? [
              <Route
                 exact
                 key={PINNED_PIPELINE_ITEMS_ROUTE_PATH}
                 path={PINNED_PIPELINE_ITEMS_ROUTE_PATH}
              >
                 <PinnedBoardRoute />
              </Route>
           ]
         : []),

      // Reminders route (conditional based on feature config)
      ...(Config.ENABLE_REMINDERS_FEATURE === 'true'
         ? [
              <Route key={REMINDERS_PATH} path={REMINDERS_PATH}>
                 <AllRemindersRoute />
              </Route>
           ]
         : []),

      // Board list route (always available)
      <Route exact key={LIST_ROUTE_PATH} path={LIST_ROUTE_PATH}>
         <BoardListRoute />
      </Route>,

      // Notifications (conditional based on feature enabled)
      ...(isNotificationsWebEnabled
         ? [
              <Route
                 exact
                 key={NOTIFICATIONS_ROUTE_PATH}
                 path={NOTIFICATIONS_ROUTE_PATH}
              >
                 <NotificationsRoute />
              </Route>
           ]
         : []),

      // GIS route (conditional based on feature enabled)
      ...(isGISTabEnabled
         ? [
              <Route exact key={GIS_ROUTE_PATH} path={GIS_ROUTE_PATH}>
                 <GISRoute />
              </Route>
           ]
         : []),
      ...(isAssignFriendEnabled
         ? [
              <Route
                 exact
                 path={APPLICATIONS_ASSIGNED_BY_FRIEND_ROUTE_PATH}
                 key={APPLICATIONS_ASSIGNED_BY_FRIEND_ROUTE_PATH}
              >
                 <ApplicationsAssignedByFriendRoute />
              </Route>
           ]
         : []),

      // Templates boards route
      <Route
         key={TEMPLATES_BOARDS_ROUTE_PATH}
         path={TEMPLATES_BOARDS_ROUTE_PATH}
      >
         <TemplatesNestedRoute />
      </Route>,
      <Route
         key={TEMPLATE_TDR_DIGITALIZATION_ROUTE_PATH}
         path={TEMPLATE_TDR_DIGITALIZATION_ROUTE_PATH}
      >
         <TDRDigitalizationBoardRoute />
      </Route>
   ]

   // Compose final routes array based on admin status
   const finalRoutes = [
      ...dashboardRoutes,
      ...(isDashboardAdmin ? [] : regularRoutes)
   ]

   return (
      <>
         <CRMItemSidePeekController />
         {isAISupportWebEnabled ? <AIActionCenter /> : null}
         <SuspenseWithLoader>
            <Switch>
               <Redirect
                  exact
                  from={`${path}`}
                  to={
                     isDashboardAdmin
                        ? ADVANCED_ANALYTICS_DASHBOARDS_ROUTE_PATH
                        : getDefaultPath()
                  }
               />
               {/* Render final composed routes */}
               {finalRoutes}
               {isSearchAndMonitoringRouteEnabled ? (
                  <Route
                     path={SEARCH_AND_MONITORING_ROUTE_PATH}
                     key={SEARCH_AND_MONITORING_ROUTE_PATH}
                  >
                     <SearchAndMonitoringRoute />
                  </Route>
               ) : null}
               {enableLegacyApplications ? (
                  <Route
                     path={LEGACY_APPLICATIONS_ROUTE_PATH}
                     key={LEGACY_APPLICATIONS_ROUTE_PATH}
                  >
                     <LegacyApplicationsRoute />
                  </Route>
               ) : null}
               {enableTDRLegacyApplications ? (
                  <Route
                     path={TDR_LEGACY_APPLICATIONS_ROUTE_PATH}
                     key={TDR_LEGACY_APPLICATIONS_ROUTE_PATH}
                  >
                     <TDRLegacyApplicationsRoute />
                  </Route>
               ) : null}
               {isPMUOfficer ? (
                  <Route path={PMU_USERS_ROUTE_PATH} key={PMU_USERS_ROUTE_PATH}>
                     <OfficersRoute />
                  </Route>
               ) : null}
               {isPMUOfficer ? (
                  <Route
                     path={PMU_USERS_LOGS_ROUTE_PATH}
                     key={PMU_USERS_LOGS_ROUTE_PATH}
                  >
                     <OfficerLogsRoute />
                  </Route>
               ) : null}

               {isApplicationManagementEnabled ? (
                  <Route
                     path={PMU_APPLICATION_MANAGEMENT_ROUTE_PATH}
                     key={PMU_APPLICATION_MANAGEMENT_ROUTE_PATH}
                  >
                     <ApplicationManagementRoute />
                  </Route>
               ) : null}
               {isTdrManagementEnabled ? (
                  <Route
                     path={ON_HOLD_TDR_CERTIFICATE_ROUTE_PATH}
                     key={ON_HOLD_TDR_CERTIFICATE_ROUTE_PATH}
                  >
                     <OnHoldTDRCertificateRoute />
                  </Route>
               ) : null}
               {isTdrManagementEnabled ? (
                  <Route
                     path={CERTIFICATES_LOGS_ROUTE_PATH}
                     key={CERTIFICATES_LOGS_ROUTE_PATH}
                  >
                     <CertificatesLogsRoute />
                  </Route>
               ) : null}
               {isTdrManagementEnabled ? (
                  <Route
                     path={RELEASE_CERTIFICATE_ROUTE_PATH}
                     key={RELEASE_CERTIFICATE_ROUTE_PATH}
                  >
                     <ReleaseTDRCertificateRoute />
                  </Route>
               ) : null}
               <Route>
                  <PageNotFound />
               </Route>
            </Switch>
         </SuspenseWithLoader>
      </>
   )
}

export default observer(HomeNestedRoutes)
